// apps/app/src/lib/utils.ts

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import i18n, { intlLocale } from './i18n';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Every instant is stored UTC. Rendering is always against the TENANT's
 * timezone, not the browser's — a clinic owner checking tomorrow's schedule
 * from a holiday must not see it shifted.
 */
export function formatDateTime(value: string | Date, timezone: string, locale = intlLocale()): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: timezone,
  }).format(new Date(value));
}

export function formatDate(value: string | Date, timezone: string, locale = intlLocale()): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: timezone }).format(
    new Date(value),
  );
}

export function formatTime(value: string | Date, timezone: string, locale = intlLocale()): string {
  return new Intl.DateTimeFormat(locale, { timeStyle: 'short', timeZone: timezone }).format(
    new Date(value),
  );
}

/**
 * Money, in the clinic's currency. Prices arrive from the API as Decimal
 * strings ("120.00"), and a record can lack a currency, so both are tolerated:
 * a missing or unknown currency formats the bare number rather than throwing,
 * which previously took the whole treatments page down.
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  locale = intlLocale(),
): string {
  const value = Number(amount ?? 0);
  const safe = Number.isFinite(value) ? value : 0;
  if (!currency) return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(safe);
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(safe);
  } catch {
    return `${currency} ${safe.toFixed(2)}`;
  }
}

export function relativeTime(value: string | Date, locale = intlLocale()): string {
  const diff = new Date(value).getTime() - Date.now();
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000_000],
    ['month', 2_592_000_000],
    ['week', 604_800_000],
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ];

  for (const [unit, ms] of units) {
    if (Math.abs(diff) >= ms) return formatter.format(Math.round(diff / ms), unit);
  }
  return formatter.format(0, 'minute');
}

export function initials(first?: string | null, last?: string | null): string {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase() || '?';
}

export function fullName(first?: string | null, last?: string | null): string {
  const name = [first, last].filter(Boolean).join(' ');
  // Anonymous scans are real and common. "Unnamed" is honest; a blank cell
  // looks like a rendering bug.
  return name || i18n.t('common:app.unnamed');
}

/** Copy with inline confirmation, never a toast — see docs/10 §9. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function debounce<T extends (...args: never[]) => void>(fn: T, ms = 250): T {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
}

export const BAND_COLORS = {
  good: '#16a34a',
  average: '#f59e0b',
  fair: '#dc2626',
} as const;
